import React from 'react';
import { CheckCircle, Clock, Truck, Package, ArrowRight } from 'lucide-react';

const statusIcon = (status) => {
  if (status === 'completed' || status === 'delivered') return <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
  if (status === 'on_the_way') return <Truck className="w-3.5 h-3.5 text-blue-500" />;
  if (status === 'assigned') return <Clock className="w-3.5 h-3.5 text-amber-500" />;
  return <Package className="w-3.5 h-3.5 text-gray-400" />;
};

/**
 * Take a full place name and return a short, human label.
 * Mapbox/Google addresses come back like:
 *   "IKEA, Saint Margaret's Road, Ballymun, Dublin, D11, Ireland"
 * The first comma-segment ("IKEA") is the most useful "short name".
 */
const shortLabel = (location, fallback) => {
  if (!location) return fallback || '—';
  const parts = String(location).split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return fallback || '—';
  return parts[0];
};

export function JobRow({ job, showPrice = true, priceField = 'assigned_price', onClick }) {
  const date = job.delivered_at ? new Date(job.delivered_at) : (job.created_at ? new Date(job.created_at) : null);
  const price = job[priceField] || job.assigned_price;
  const total = (job.assigned_price || 0) + (job.platform_fee || 0);
  const isCompleted = job.status === 'completed';
  const clickable = !!onClick;

  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? {
        type: 'button',
        onClick: () => onClick(job),
        'data-testid': `job-row-${job.id}`,
        className:
          'w-full text-left flex items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer',
      }
    : {
        'data-testid': `job-row-${job.id}`,
        className:
          'flex items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors',
      };

  return (
    <Wrapper {...wrapperProps}>
      {/* Status icon */}
      <div className="flex-shrink-0 w-4">{statusIcon(job.status)}</div>

      {/* Date */}
      <div className="w-20 sm:w-24 flex-shrink-0">
        {date ? (
          <>
            <p className="text-xs font-medium text-gray-700">
              {date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}
            </p>
            <p className="text-[10px] text-gray-400">
              {date.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </>
        ) : <span className="text-xs text-gray-300">—</span>}
      </div>

      {/* Route + driver */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm">
          <span
            className="font-semibold text-gray-900 truncate max-w-[120px] sm:max-w-[160px]"
            title={job.pickup_location || job.pickup_county}
          >
            {shortLabel(job.pickup_location, job.pickup_county)}
          </span>
          <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
          <span
            className="text-gray-700 truncate max-w-[120px] sm:max-w-[160px]"
            title={job.dropoff_location || job.dropoff_county}
          >
            {shortLabel(job.dropoff_location, job.dropoff_county)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
          {job.delivery_ref && (
            <span className="inline-block font-mono font-semibold text-[10px] text-[#1B5E20] bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded" data-testid={`row-ref-${job.id}`}>
              {job.delivery_ref}
            </span>
          )}
          {/* Pay Driver Directly: the only payment pill shown is the direct
              one. Legacy online-paid bookings show no payment pill. */}
          {job.payment_method === 'direct' && (
            <span
              className="inline-block font-semibold text-[10px] text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded"
              data-testid={`row-payment-method-${job.id}`}
              title="Paid directly to the driver — Cash / Revolut / Bank"
            >
              Paid direct
            </span>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium" data-testid={`row-delivered-${job.id}`}>
              <CheckCircle className="w-3 h-3" /> Delivered
            </span>
          )}
          {job.assigned_driver_name && (
            <span className="truncate" data-testid={`row-driver-${job.id}`}>· {job.assigned_driver_name}</span>
          )}
        </div>
      </div>

      {/* Item type — desktop only */}
      <div className="hidden md:block w-20 flex-shrink-0">
        {job.item_type && (
          <span className="text-[10px] font-medium text-gray-500 capitalize bg-gray-100 px-1.5 py-0.5 rounded">{job.item_type}</span>
        )}
      </div>

      {/* Price */}
      {showPrice && (
        <div className="w-20 flex-shrink-0 text-right">
          {price ? (
            <>
              <p className="text-sm font-bold text-gray-900" data-testid={`row-price-${job.id}`}>€{(total || price).toFixed(2)}</p>
              {isCompleted && <p className="text-[10px] text-emerald-600 font-medium">Paid</p>}
            </>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>
      )}
    </Wrapper>
  );
}

export function JobRowHeader({ showPrice = true }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-200 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400 font-medium">
      <div className="w-4 flex-shrink-0"></div>
      <div className="w-20 sm:w-24 flex-shrink-0">Date</div>
      <div className="flex-1">Route &amp; driver</div>
      <div className="hidden md:block w-20 flex-shrink-0">Type</div>
      {showPrice && <div className="w-20 flex-shrink-0 text-right">Total</div>}
    </div>
  );
}
